import { useState } from 'react';
import { useTracking } from 'react-tracking';
import loadable from '@loadable/component';

import { Button } from '@simplywallst/ui-core';
import CheckSolid from '@simplywallst/ui-core/icons/CheckSolid';
import PlusSolid from '@simplywallst/ui-core/icons/PlusSolid';

import { useUser } from '@/hooks/useUser';
import {
  Watchlist,
  Watchlists,
  useWatchlist,
  useWatchlists,
} from '@/hooks/useWatchlist';
import If from '@components/If';

import { doesCompanyExistInWatchlist } from '@/pages/Watchlist/utils';
import useWatchlistMutations from '@/pages/Watchlist/hooks/useWatchlistMutations';
import { useFollowedNarratives } from '@/pages/Narrative/hooks/useFollowedNarratives';
import { useFollowNarrative } from '@/pages/Narrative/hooks/useFollowNarrative';
import { useUnfollowNarrative } from '@/pages/Narrative/hooks/useUnfollowNarrative';

const LimitModal = loadable(
  () => import('@/pages/Watchlist/components/LimitModal')
);

const AddToWatchlistsModal = loadable(
  () =>
    import(
      '@/components/WatchlistAddRemoveCompanyToggle/components/AddToWatchlistsModal'
    )
);

type Modal = 'watchlist-limit-modal' | 'add-to-watchlists-modal';

export type Narrative = {
  id: string;
  author: {
    id: string;
  };
  company: {
    id: string;
    tickerSymbol: string;
  } | null;
};

type Props = {
  narrative: Narrative;
};

export const AuthenticatedFollowNarrative = ({ narrative }: Props) => {
  const { data: user } = useUser();
  const [modal, setModal] = useState<Modal | null>(null);
  const { trackEvent } = useTracking({
    subject: 'followNarrative',
    type: 'track',
    userId: user?.id,
  });
  const {
    data: followedNarratives,
    isLoading: isLoadingFollowedNarratives,
    error: followedNarrativesError,
  } = useFollowedNarratives();
  const { mutate: followNarrative, isPending: isLoadingFollowNarrative } =
    useFollowNarrative();
  const { mutate: unfollowNarrative, isPending: isLoadingUnfollowNarrative } =
    useUnfollowNarrative();
  const {
    data: watchlist,
    isLoading: isLoadingWatchlist,
    error: watchlistError,
  } = useWatchlist();
  const {
    data: watchlists,
    isLoading: isLoadingWatchlists,
    error: watchlistsError,
  } = useWatchlists();
  const { addCompany, addCompanyStatus } = useWatchlistMutations();

  if (
    followedNarrativesError ||
    watchlistError ||
    watchlistsError ||
    typeof narrative === 'undefined'
  ) {
    return null;
  }

  if (narrative.author.id === user.id) {
    return null;
  }

  const followedNarrative = followedNarratives?.find(
    (n) => n.followedNarrativeId === narrative.id
  );
  const hasFollowedNarrative = typeof followedNarrative !== 'undefined';

  const isLoading =
    isLoadingFollowedNarratives ||
    isLoadingWatchlist ||
    isLoadingWatchlists ||
    isLoadingFollowNarrative ||
    isLoadingUnfollowNarrative ||
    addCompanyStatus === 'pending';

  const handleClick = () => {
    if (hasFollowedNarrative) {
      unfollowNarrative({ narrativeId: narrative.id });
      trackEvent({ modifier: 'unfollow', action: 'click' });
    } else {
      const hasMultipleWatchlists =
        typeof watchlists !== 'undefined' && watchlists.length > 1;
      const watchlistKeys = watchlist?.items.map((n) => n.companyId);
      const watchlistsKeys = watchlists?.flatMap((w) =>
        w.items.map((n) => n.companyId)
      );
      const existsInSomeWatchlist = doesCompanyExistInWatchlist(
        narrative?.company?.id,
        hasMultipleWatchlists ? watchlistsKeys : watchlistKeys
      );

      followNarrative({ narrativeId: narrative.id });
      trackEvent({ modifier: 'follow', action: 'click' });

      if (!existsInSomeWatchlist && hasMultipleWatchlists) {
        setModal('add-to-watchlists-modal');
      }

      if (
        !existsInSomeWatchlist &&
        !hasMultipleWatchlists &&
        watchlist &&
        watchlist.items.length === watchlist.itemsLimit
      ) {
        setModal('watchlist-limit-modal');
      }

      if (
        !existsInSomeWatchlist &&
        !hasMultipleWatchlists &&
        typeof watchlist !== 'undefined' &&
        watchlist.items.length < watchlist.itemsLimit &&
        narrative.company
      ) {
        addCompany({
          id: narrative.company?.id,
          watchlistId: watchlist?.id,
        });
      }
    }
  };

  return (
    <>
      <If match={modal === 'watchlist-limit-modal' && Boolean(watchlist)}>
        {() => (
          <LimitModal
            watchlist={watchlist as Watchlist}
            onClose={() => setModal(null)}
          />
        )}
      </If>
      <If
        match={
          modal === 'add-to-watchlists-modal' &&
          Boolean(narrative.company) &&
          Boolean(watchlists)
        }
      >
        {() => (
          <AddToWatchlistsModal
            companyId={narrative.company?.id as string}
            tickerSymbol={narrative.company?.tickerSymbol as string}
            watchlists={watchlists as Watchlists}
            onClose={() => setModal(null)}
          />
        )}
      </If>
      <Button
        alignIcon="left"
        disabled={isLoading}
        isLoading={isLoading}
        display="inline"
        onClick={() => handleClick()}
        styleType="loud"
      >
        {hasFollowedNarrative ? <CheckSolid /> : <PlusSolid />}
        {hasFollowedNarrative ? 'Following' : 'Follow Narrative'}
      </Button>
    </>
  );
};
